import { Box, Center, Image, Loader } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import PdfPreview from "components/PdfPreview";
import { memo, useEffect, useState } from "react";

interface DocumentPreviewProps {
  crop?: "cover" | "contain";
  isRounded?: boolean;
  width?: number | string;
  height?: number;
  document?: Blob;
  isPdf?: boolean;
  removable?: boolean;
  children?: React.ReactNode;
  borderColor?: string;
  borderSize?: number;
  handleRemoveDocument?: () => void;
  onClick?: () => void;
  loading?: boolean;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  crop = "cover",
  isRounded = true,
  width,
  height,
  handleRemoveDocument,
  onClick,
  isPdf,
  document,
  removable = true,
  borderColor = "#000",
  borderSize = 2,
  loading,
  children,
}) => {
  const [preview, setPreview] = useState<string>("");

  useEffect(() => {
    if (!(document instanceof Blob)) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(document);
    setPreview(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [document]);

  if (loading) {
    return (
      <Box
        onClick={onClick}
        className="relative cursor-pointer"
        h={height ?? "100%"}
        w={width ?? "100%"}
      >
        <Box
          h={height ?? "100%"}
          w={width ?? "100%"}
          className={`w-full h-full overflow-hidden bg-white ${
            isRounded ? "rounded-md" : ""
          }`}
          style={{ borderColor, borderWidth: borderSize, borderStyle: "solid" }}
        >
          <Center h={"100%"} w={"100%"}>
            <Loader className="m-auto" />
          </Center>
        </Box>
      </Box>
    );
  }

  if (!document) {
    return null;
  }

  return (
    <Box
      onClick={onClick}
      className="relative cursor-pointer"
      h={height ?? "100%"}
      w={width ?? "100%"}
    >
      <Box
        h={height ?? "100%"}
        w={width ?? "100%"}
        className={`w-full h-full overflow-hidden ${
          isRounded ? "rounded-md" : ""
        }`}
        style={{ borderColor, borderWidth: borderSize, borderStyle: "solid" }}
      >
        {removable && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              handleRemoveDocument?.();
            }}
            className={
              "absolute z-10 -top-3 -right-3 flex items-center rounded-full p-0.5 bg-white border-2 border-black"
            }
          >
            <IconX strokeWidth={3} size={20} color="#000" />
          </button>
        )}

        {children}

        {isPdf ? (
          <PdfPreview preview={preview} />
        ) : (
          preview && (
            <Image
              src={preview}
              fit={crop}
              h={"100%"}
              w={"100%"}
            />
          )
        )}
      </Box>
    </Box>
  );
};

export default memo(DocumentPreview);
